import React, { Component, ErrorInfo } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { captureException } from '@tink/core/src/sentry';
import { AppStateErrorType, ErrorReason, ErrorStatus } from '@tink/core/src/tinkLinkErrorCodes';
import {
  ErrorNotification as ErrorNotificationImport,
  ErrorNotificationPresenterOwnProps,
} from '../../core/screens/ErrorNotification/ErrorNotification';
import { AppState } from '../../stores/redux-store';
import { AppStoreCredentialsError, AppStoreGeneralError } from '../../stores/app-state-errors';
import { ApplicationError, ErrorWithType } from './errors/ApplicationError';

export const UNEXPECTED_ERROR = {
  errorType: AppStateErrorType.GENERAL_ERROR,
  errorStatus: ErrorStatus.INTERNAL_ERROR,
  errorReason: ErrorReason.UNEXPECTED_ERROR,
} as const;

const isCredentialsError = (error: ErrorWithType): error is AppStoreCredentialsError =>
  error.errorType === AppStateErrorType.CREDENTIALS && !!error.credentialsId;
class ApplicationErrorBoundaryPresenter extends Component<
  {
    children: React.ReactNode;
    ErrorNotification?: React.ComponentType<ErrorNotificationPresenterOwnProps>;
  } & PropsFromRedux,
  { error?: AppStoreGeneralError | AppStoreCredentialsError }
> {
  state: { error?: AppStoreGeneralError | AppStoreCredentialsError } = { error: undefined };

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    if (error instanceof ApplicationError) {
      if (isCredentialsError(error.commonError)) {
        this.setState({
          error: error.commonError,
        });
      } else {
        this.setState({
          error: {
            ...error.commonError,
            errorType: AppStateErrorType.GENERAL_ERROR,
          },
        });
      }
    } else {
      captureException(error, { custom: { ...errorInfo } });

      this.setState({
        error: UNEXPECTED_ERROR,
      });
    }
  }

  render() {
    const { ErrorNotification = ErrorNotificationImport } = this.props;

    if (this.state.error) {
      return <ErrorNotification error={this.state.error} />;
    }

    if (this.props.applicationError) {
      return <ErrorNotification error={this.props.applicationError} />;
    }

    return this.props.children;
  }
}

const mapStateToProps = (state: AppState) => {
  return {
    applicationError: state.application.error,
  };
};

const connector = connect(mapStateToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export const ApplicationErrorBoundary = connector(ApplicationErrorBoundaryPresenter);
